import React, { useState } from 'react';

import { DialogContent, Typography } from '@mui/material';
import {
  AlineDialog,
  AlineDialogButtons,
  DocumentPicker,
  DocumentPickerVariant,
} from 'src/components';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  buttons: {
    flexShrink: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '75vh',
    maxHeight: 'calc(100vh - 64px)',
    maxWidth: 1080,
    minWidth: '75%',
  },
  content: {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    boxSizing: 'border-box',
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minHeight: 0,
    scrollbarWidth: 'none',
    width: '100%',
  },
  subtitle: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
    fontWeight: 400,
    letterSpacing: 0,
  },
}));

export interface SelectRelatedDocumentDialogProps {
  onClose: () => void;
  onSelect: (relatedDocumentId: UUID) => Promise<void> | void;
  open: boolean;
  repositoryId: UUID;
  documentVariant?: Apollo.DocumentVariant;
  excludeDocumentId?: UUID;
  sourceDocumentName?: string;
  topContent?: React.ReactNode;
}

export function SelectRelatedDocumentDialog({
  documentVariant = Apollo.DocumentVariant.Document,
  excludeDocumentId,
  onClose,
  onSelect,
  open,
  repositoryId,
  sourceDocumentName,
  topContent,
}: SelectRelatedDocumentDialogProps): JSX.Element {
  const { classes } = useStyles();
  const [submitting, setSubmitting] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<UUID | null>(null);

  function handleClose(): void {
    setSelectedDocumentId(null);
    setSubmitting(false);
    onClose();
  }

  async function handleDone(): Promise<void> {
    if (!selectedDocumentId) return;

    try {
      setSubmitting(true);
      await onSelect(selectedDocumentId);
      handleClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AlineDialog
      classes={{ paperWidthMd: classes.container }}
      contentPaddingBottomPx={24}
      contentPaddingTopPx={8}
      disableAltClose={submitting}
      loading={submitting}
      open={open}
      title="Select Related Document"
      onClose={handleClose}
    >
      <DialogContent className={classes.content}>
        {sourceDocumentName && (
          <Typography className={classes.subtitle} variant="body2">
            {`You're linking a document to "${sourceDocumentName}"`}
          </Typography>
        )}
        {topContent}
        <DocumentPicker
          showFiltersButton
          documentVariant={documentVariant}
          getIsRowDisabled={row => {
            if (!excludeDocumentId || row.id !== excludeDocumentId)
              return { isRowDisabled: false, reason: '' };

            return { isRowDisabled: true, reason: "Can't link a document to itself." };
          }}
          pickerVariant={DocumentPickerVariant.SingleSelect}
          repositoryId={repositoryId}
          searchPlaceholder="Search documents..."
          onSelectionChange={ids => setSelectedDocumentId(ids.at(0) ?? null)}
        />
      </DialogContent>
      <AlineDialogButtons
        cancelHandler={handleClose}
        className={classes.buttons}
        disabled={!selectedDocumentId}
        loading={submitting}
        submitHandler={handleDone}
        submitText="Link Document"
      />
    </AlineDialog>
  );
}
